// /src/components/tools/memberProfileCard/PerkThemeSnowCanvas.jsx
// Realistic falling snow — soft flakes, depth layers, density + wind drift/gusts.

import React, { useEffect, useRef } from 'react';

function clamp01(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function clampQty(value, fallback = 50) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function createRng(seed) {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Build snowflake set. Depth z: 0 = far (small/slow), 1 = near (large/faster).
 */
function buildFlakes(density, wind, seed) {
  const rng = createRng(seed);
  const d = clamp01(density / 100);
  const w = clamp01(wind / 100);
  // ~35 far dust → ~280 mid/near at full density
  const count = Math.round(35 + d * 245);

  const flakes = [];
  for (let i = 0; i < count; i += 1) {
    const z = rng() ** 1.35; // bias toward smaller/far flakes
    const roll = rng();
    let radius;
    if (z < 0.35) radius = 0.3 + rng() * 0.4;
    else if (z < 0.7) radius = 0.55 + rng() * 0.75;
    else radius = 0.95 + rng() * 1.15;
    // Near flakes a bit larger
    radius *= 0.65 + z * 0.55;

    flakes.push({
      x: rng(),
      y: rng(),
      z,
      radius: Math.min(2.4, radius),
      // Base fall speed (normalized units / sec relative to height)
      vy: (0.045 + z * 0.11 + rng() * 0.04) * (0.85 + d * 0.25),
      // Horizontal sway amplitude / phase
      swayAmp: (0.008 + z * 0.02) * (0.4 + rng()),
      swayFreq: 0.35 + rng() * 0.9,
      swayPhase: rng() * Math.PI * 2,
      // Wind response: near flakes catch more wind
      windMul: 0.35 + z * 0.9 + rng() * 0.2,
      alpha: 0.28 + z * 0.45 + rng() * 0.2,
      soft: roll > 0.55 || z > 0.55,
      spin: (rng() - 0.5) * (0.4 + w * 1.2),
      angle: rng() * Math.PI * 2,
    });
  }
  return flakes;
}

export default function PerkThemeSnowCanvas({
  density = 55,
  wind = 35,
}) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const flakesRef = useRef([]);
  const rafRef = useRef(0);
  const dens = clampQty(density, 55);
  const wnd = clampQty(wind, 35);

  useEffect(() => {
    const seed = 0x50a1f1a ^ (dens * 7919) ^ (wnd * 104729);
    flakesRef.current = buildFlakes(dens, wnd, seed >>> 0);
  }, [dens, wnd]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const windT = clamp01(wnd / 100);
    // Steady drift to the right; gusts layer on top
    const baseWind = (0.02 + windT * 0.22);
    let gust = 0;
    let gustTarget = 0;
    let gustTimer = 0;

    let last = performance.now();
    let running = true;
    let tSec = 0;

    const resize = () => {
      const w = Math.max(1, wrap.clientWidth || 1);
      const h = Math.max(1, wrap.clientHeight || 1);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cw = Math.max(1, Math.round(w * dpr));
      const ch = Math.max(1, Math.round(h * dpr));
      if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw;
        canvas.height = ch;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const drawFlake = (f, px, py) => {
      const r = f.radius;
      const a = Math.max(0.08, Math.min(0.95, f.alpha));
      if (f.soft && r > 1.1) {
        const g = ctx.createRadialGradient(px, py, 0, px, py, r * 1.35);
        g.addColorStop(0, `rgba(255, 255, 255, ${a.toFixed(3)})`);
        g.addColorStop(0.45, `rgba(235, 245, 255, ${(a * 0.55).toFixed(3)})`);
        g.addColorStop(1, 'rgba(220, 235, 255, 0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(px, py, r * 1.35, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.fillStyle = `rgba(248, 252, 255, ${a.toFixed(3)})`;
        ctx.arc(px, py, r * 0.55, 0, Math.PI * 2);
        ctx.fill();
      }

      // Soft elongated streak when wind is strong (motion blur hint)
      if (windT > 0.35 && f.z > 0.45) {
        const streak = (windT - 0.35) * f.radius * 2.2 * f.windMul;
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(f.angle * 0.15);
        const sg = ctx.createLinearGradient(-streak, 0, streak * 0.2, 0);
        sg.addColorStop(0, 'rgba(255,255,255,0)');
        sg.addColorStop(0.7, `rgba(245,250,255, ${(a * 0.25).toFixed(3)})`);
        sg.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = sg;
        ctx.fillRect(-streak, -r * 0.25, streak * 1.2, r * 0.5);
        ctx.restore();
      }
    };

    const draw = (now) => {
      if (!running) return;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      tSec += dt;

      // Gusts: stronger / more frequent with wind slider
      gustTimer -= dt;
      if (gustTimer <= 0) {
        gustTarget = windT <= 0.05
          ? 0
          : (Math.random() * 2 - 0.35) * windT * (0.55 + Math.random() * 0.7);
        gustTimer = 0.35 + Math.random() * (1.8 - windT * 1.1);
      }
      gust += (gustTarget - gust) * Math.min(1, dt * (1.8 + windT * 2.5));

      const windNow = baseWind + gust;

      const w = Math.max(1, wrap.clientWidth || 1);
      const h = Math.max(1, wrap.clientHeight || 1);
      ctx.clearRect(0, 0, w, h);

      // Very light cold haze at high density (readable on dark media)
      if (dens > 70) {
        const haze = ((dens - 70) / 30) * 0.06;
        ctx.fillStyle = `rgba(210, 230, 255, ${haze.toFixed(3)})`;
        ctx.fillRect(0, 0, w, h);
      }

      const flakes = flakesRef.current;
      for (let i = 0; i < flakes.length; i += 1) {
        const f = flakes[i];
        const sway = Math.sin(tSec * f.swayFreq + f.swayPhase) * f.swayAmp;
        const vx = windNow * f.windMul + sway;

        f.x += vx * dt;
        f.y += f.vy * dt;
        f.angle += f.spin * dt;

        // Wrap with spawn margin so wind doesn't leave empty edges
        if (f.y > 1.08) {
          f.y = -0.06 - Math.random() * 0.08;
          f.x = Math.random();
        }
        if (f.x > 1.12) f.x -= 1.24;
        if (f.x < -0.12) f.x += 1.24;

        drawFlake(f, f.x * w, f.y * h);
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    resize();
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => resize())
      : null;
    if (ro) ro.observe(wrap);

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      running = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) ro.disconnect();
    };
  }, [dens, wnd]);

  return (
    <div
      ref={wrapRef}
      className="perk-theme-overlay__layer perk-theme-overlay__layer--snow"
      aria-hidden
    >
      <canvas ref={canvasRef} className="perk-theme-overlay__snow-canvas" />
    </div>
  );
}
